import { supabase } from '../../integrations/supabase/client';
import type { EmailLog, EmailLogStats, IEmailLogRepository } from '../interfaces/IEmailLogRepository';

export class SupabaseEmailLogRepository implements IEmailLogRepository {
  async getAll(filters?: {
    status?: EmailLog['status'];
    recipient?: string;
    hub_template_id?: number;
    related_entity_id?: string;
    start_date?: string;
    end_date?: string;
    page?: number;
    limit?: number;
  }): Promise<{ logs: EmailLog[]; total: number }> {
    const page = filters?.page || 1;
    const limit = filters?.limit || 50;
    const offset = (page - 1) * limit;

    let query = (supabase as any)
      .from('email_logs')
      .select(`
        *,
        hub_templates(name)
      `, { count: 'exact' });

    if (filters?.status) {
      query = query.eq('status', filters.status);
    }
    if (filters?.recipient) {
      query = query.ilike('recipient_email', `%${filters.recipient}%`);
    }
    if (filters?.hub_template_id) {
      query = query.eq('hub_template_id', filters.hub_template_id);
    }
    if (filters?.related_entity_id) {
      query = query.or(`tenant_id.eq.${filters.related_entity_id},onboarding_flow_id.eq.${filters.related_entity_id}`);
    }
    if (filters?.start_date) {
      query = query.gte('sent_at', filters.start_date);
    }
    if (filters?.end_date) {
      query = query.lte('sent_at', filters.end_date);
    }

    const { data, error, count } = await query
      .range(offset, offset + limit - 1)
      .order('sent_at', { ascending: false });

    if (error) throw error;

    const logs: EmailLog[] = (data || []).map((log: any) => ({
      id: log.id,
      hub_template_id: log.hub_template_id,
      template_name: (log.hub_templates as any)?.name || log.metadata?.template_name || 'Unknown Template',
      recipient_email: log.recipient_email,
      recipient_name: log.metadata?.recipient_name,
      subject: log.subject,
      body: log.body,
      status: log.status,
      sent_at: log.sent_at,
      delivered_at: log.delivered_at,
      opened_at: log.opened_at,
      clicked_at: log.clicked_at,
      bounce_reason: log.error_message,
      error_message: log.error_message,
      sent_by: log.sent_by,
      related_entity_type: log.tenant_id ? 'tenant' : log.onboarding_flow_id ? 'onboarding_flow' : undefined,
      related_entity_id: log.tenant_id || log.onboarding_flow_id,
      metadata: log.metadata,
    }));

    return {
      logs,
      total: count || 0,
    };
  }

  async getById(id: string): Promise<EmailLog | null> {
    const { data, error } = await (supabase as any)
      .from('email_logs')
      .select(`
        *,
        hub_templates(name)
      `)
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }

    return {
      id: data.id,
      hub_template_id: data.hub_template_id,
      template_name: (data.hub_templates as any)?.name || data.metadata?.template_name || 'Unknown Template',
      recipient_email: data.recipient_email,
      recipient_name: data.metadata?.recipient_name,
      subject: data.subject,
      body: data.body,
      status: data.status,
      sent_at: data.sent_at,
      delivered_at: data.delivered_at,
      opened_at: data.opened_at,
      clicked_at: data.clicked_at,
      bounce_reason: data.error_message,
      error_message: data.error_message,
      sent_by: data.sent_by,
      related_entity_type: data.tenant_id ? 'tenant' : data.onboarding_flow_id ? 'onboarding_flow' : undefined,
      related_entity_id: data.tenant_id || data.onboarding_flow_id,
      metadata: data.metadata,
    };
  }

  async getByRecipient(email: string): Promise<EmailLog[]> {
    const { data, error } = await (supabase as any)
      .from('email_logs')
      .select(`
        *,
        hub_templates(name)
      `)
      .eq('recipient_email', email)
      .order('sent_at', { ascending: false });

    if (error) throw error;

    return (data || []).map((log: any) => ({
      id: log.id,
      hub_template_id: log.hub_template_id,
      template_name: (log.hub_templates as any)?.name || log.metadata?.template_name || 'Unknown Template',
      recipient_email: log.recipient_email,
      recipient_name: log.metadata?.recipient_name,
      subject: log.subject,
      body: log.body,
      status: log.status,
      sent_at: log.sent_at,
      delivered_at: log.delivered_at,
      opened_at: log.opened_at,
      clicked_at: log.clicked_at,
      bounce_reason: log.error_message,
      error_message: log.error_message,
      sent_by: log.sent_by,
      related_entity_type: log.tenant_id ? 'tenant' : log.onboarding_flow_id ? 'onboarding_flow' : undefined,
      related_entity_id: log.tenant_id || log.onboarding_flow_id,
      metadata: log.metadata,
    }));
  }

  async getByTemplate(templateId: number): Promise<EmailLog[]> {
    const { data, error } = await (supabase as any)
      .from('email_logs')
      .select(`
        *,
        hub_templates(name)
      `)
      .eq('hub_template_id', templateId)
      .order('sent_at', { ascending: false });

    if (error) throw error;

    return (data || []).map((log: any) => ({
      id: log.id,
      hub_template_id: log.hub_template_id,
      template_name: (log.hub_templates as any)?.name || log.metadata?.template_name || 'Unknown Template',
      recipient_email: log.recipient_email,
      recipient_name: log.metadata?.recipient_name,
      subject: log.subject,
      body: log.body,
      status: log.status,
      sent_at: log.sent_at,
      delivered_at: log.delivered_at,
      opened_at: log.opened_at,
      clicked_at: log.clicked_at,
      bounce_reason: log.error_message,
      error_message: log.error_message,
      sent_by: log.sent_by,
      related_entity_type: log.tenant_id ? 'tenant' : log.onboarding_flow_id ? 'onboarding_flow' : undefined,
      related_entity_id: log.tenant_id || log.onboarding_flow_id,
      metadata: log.metadata,
    }));
  }

  async getStats(filters?: {
    start_date?: string;
    end_date?: string;
    hub_template_id?: number;
  }): Promise<EmailLogStats> {
    let query = (supabase as any).from('email_logs').select('status', { count: 'exact', head: true });

    if (filters?.start_date) query = query.gte('sent_at', filters.start_date);
    if (filters?.end_date) query = query.lte('sent_at', filters.end_date);
    if (filters?.hub_template_id) query = query.eq('hub_template_id', filters.hub_template_id);

    const { count: totalSent } = await query;

    const { count: totalDelivered } = await query.eq('status', 'delivered');
    const { count: totalBounced } = await query.eq('status', 'bounced');
    const { count: totalOpened } = await query.eq('status', 'opened');
    const { count: totalClicked } = await query.eq('status', 'clicked');

    const sent = totalSent || 0;
    const delivered = totalDelivered || 0;
    const bounced = totalBounced || 0;
    const opened = totalOpened || 0;
    const clicked = totalClicked || 0;

    return {
      total_sent: sent,
      total_delivered: delivered,
      total_bounced: bounced,
      total_opened: opened,
      total_clicked: clicked,
      delivery_rate: sent > 0 ? (delivered / sent) * 100 : 0,
      open_rate: delivered > 0 ? (opened / delivered) * 100 : 0,
      click_rate: opened > 0 ? (clicked / opened) * 100 : 0,
      bounce_rate: sent > 0 ? (bounced / sent) * 100 : 0,
    };
  }

  async create(data: {
    hub_template_id?: number;
    template_name: string;
    recipient_email: string;
    recipient_name?: string;
    subject: string;
    body: string;
    sent_by: string;
    related_entity_type?: EmailLog['related_entity_type'];
    related_entity_id?: string;
    metadata?: Record<string, any>;
  }): Promise<EmailLog> {
    const logData: any = {
      hub_template_id: data.hub_template_id,
      recipient_email: data.recipient_email,
      subject: data.subject,
      body: data.body,
      status: 'sent',
      sent_by: data.sent_by,
      metadata: { ...data.metadata, recipient_name: data.recipient_name },
    };

    if (data.related_entity_type === 'tenant') {
      logData.tenant_id = data.related_entity_id;
    } else if (data.related_entity_type === 'onboarding_flow') {
      logData.onboarding_flow_id = data.related_entity_id;
    }

    const { data: log, error } = await (supabase as any)
      .from('email_logs')
      .insert(logData)
      .select()
      .single();

    if (error) throw error;

    return {
      id: log.id,
      hub_template_id: log.hub_template_id,
      template_name: data.template_name,
      recipient_email: log.recipient_email,
      recipient_name: data.recipient_name,
      subject: log.subject,
      body: log.body,
      status: log.status,
      sent_at: log.sent_at,
      sent_by: log.sent_by,
      related_entity_type: data.related_entity_type,
      related_entity_id: data.related_entity_id,
      metadata: log.metadata,
    };
  }

  async updateStatus(
    id: string,
    status: EmailLog['status'],
    metadata?: {
      bounce_reason?: string;
      error_message?: string;
    }
  ): Promise<EmailLog> {
    const updateData: any = { status };
    
    if (status === 'delivered') {
      updateData.delivered_at = new Date().toISOString();
    } else if (status === 'opened') {
      updateData.opened_at = new Date().toISOString();
    } else if (status === 'clicked') {
      updateData.clicked_at = new Date().toISOString();
    } else if (status === 'bounced') {
      updateData.bounced_at = new Date().toISOString();
      if (metadata?.bounce_reason) updateData.error_message = metadata.bounce_reason;
    } else if (status === 'failed') {
      if (metadata?.error_message) updateData.error_message = metadata.error_message;
    }

    const { data, error } = await (supabase as any)
      .from('email_logs')
      .update(updateData)
      .eq('id', id)
      .select(`
        *,
        hub_templates(name)
      `)
      .single();

    if (error) throw error;

    return {
      id: data.id,
      hub_template_id: data.hub_template_id,
      template_name: (data.hub_templates as any)?.name || data.metadata?.template_name || 'Unknown Template',
      recipient_email: data.recipient_email,
      recipient_name: data.metadata?.recipient_name,
      subject: data.subject,
      body: data.body,
      status: data.status,
      sent_at: data.sent_at,
      delivered_at: data.delivered_at,
      opened_at: data.opened_at,
      clicked_at: data.clicked_at,
      bounce_reason: data.error_message,
      error_message: data.error_message,
      sent_by: data.sent_by,
      related_entity_type: data.tenant_id ? 'tenant' : data.onboarding_flow_id ? 'onboarding_flow' : undefined,
      related_entity_id: data.tenant_id || data.onboarding_flow_id,
      metadata: data.metadata,
    };
  }

  async getRecentActivity(limit: number = 10): Promise<EmailLog[]> {
    const { data, error } = await (supabase as any)
      .from('email_logs')
      .select(`
        *,
        hub_templates(name)
      `)
      .order('sent_at', { ascending: false })
      .limit(limit);

    if (error) throw error;

    return (data || []).map((log: any) => ({
      id: log.id,
      hub_template_id: log.hub_template_id,
      template_name: (log.hub_templates as any)?.name || log.metadata?.template_name || 'Unknown Template',
      recipient_email: log.recipient_email,
      recipient_name: log.metadata?.recipient_name,
      subject: log.subject,
      body: log.body,
      status: log.status,
      sent_at: log.sent_at,
      delivered_at: log.delivered_at,
      opened_at: log.opened_at,
      clicked_at: log.clicked_at,
      bounce_reason: log.error_message,
      error_message: log.error_message,
      sent_by: log.sent_by,
      related_entity_type: log.tenant_id ? 'tenant' : log.onboarding_flow_id ? 'onboarding_flow' : undefined,
      related_entity_id: log.tenant_id || log.onboarding_flow_id,
      metadata: log.metadata,
    }));
  }

  async getDeliveryTimeline(days: number = 7): Promise<Array<{ date: string; sent: number; delivered: number; opened: number }>> {
    const timeline: Array<{ date: string; sent: number; delivered: number; opened: number }> = [];
    const today = new Date();

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      date.setHours(0, 0, 0, 0);
      
      const nextDate = new Date(date);
      nextDate.setDate(nextDate.getDate() + 1);

      const { count: sent } = await (supabase as any)
        .from('email_logs')
        .select('*', { count: 'exact', head: true })
        .gte('sent_at', date.toISOString())
        .lt('sent_at', nextDate.toISOString());

      const { count: delivered } = await (supabase as any)
        .from('email_logs')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'delivered')
        .gte('sent_at', date.toISOString())
        .lt('sent_at', nextDate.toISOString());

      const { count: opened } = await (supabase as any)
        .from('email_logs')
        .select('*', { count: 'exact', head: true })
        .in('status', ['opened', 'clicked'])
        .gte('sent_at', date.toISOString())
        .lt('sent_at', nextDate.toISOString());

      timeline.push({
        date: date.toISOString().split('T')[0],
        sent: sent || 0,
        delivered: delivered || 0,
        opened: opened || 0,
      });
    }

    return timeline;
  }
}
